import apiClient from '../apiClient';
import { API_ENDPOINTS } from '../endpoints';

/* =========================================================
   TYPES
========================================================= */

export type SpinPrizeType = 'money' | 'item' | 'nothing' | 'spin_again';
export type SpinWinStatus = 'completed' | 'pending' | 'rejected';

export interface SpinPrizeItem {
  _id: string;
  label: string;
  labelAlt: string;
  type: SpinPrizeType;
  amount: number;
  probability: number;
  isActive: boolean;
  dailyLimit: number | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSpinPrizeInput {
  label: string;
  labelAlt?: string;
  type: SpinPrizeType;
  amount?: number;
  probability?: number;
  isActive?: boolean;
  dailyLimit?: number | null;
  sortOrder?: number;
}

export type UpdateSpinPrizeInput = Partial<CreateSpinPrizeInput>;

export interface SpinWinListItem {
  id: string;
  userId: string | null;
  telegramId: string;
  userName: string;
  prizeId: string | null;
  prizeLabel: string;
  prizeLabelAlt: string;
  prizeType: SpinPrizeType;
  amount: number;
  currency: string;
  balanceAfter: number;
  status: SpinWinStatus;
  consumedCoin: boolean;
  note: string;
  createdAt: string;
}

export interface SpinWinDetail extends SpinWinListItem {
  userUsername: string;
  userPhone: string;
  userBalance: number;
  updatedAt: string;
}

export interface SpinWinStats {
  totalWins: number;
  todayWins: number;
  totalMoneyDistributed: number;
  pendingCount: number;
  completedCount: number;
  rejectedCount: number;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface SpinWinsResponse {
  wins: SpinWinListItem[];
  pagination: Pagination;
}

export interface GetSpinWinsParams {
  page?: number;
  limit?: number;
  search?: string;
  prizeType?: 'all' | SpinPrizeType;
  status?: 'all' | SpinWinStatus;
  minDate?: string;
  maxDate?: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

/* =========================================================
   SERVICE
========================================================= */

class SpinService {
  /* Prizes */
  async getAllPrizes(): Promise<SpinPrizeItem[]> {
    const res = await apiClient.get<ApiEnvelope<SpinPrizeItem[]>>(
      API_ENDPOINTS.SPIN.GET_PRIZES
    );
    return res.data.data;
  }

  async createPrize(input: CreateSpinPrizeInput): Promise<SpinPrizeItem> {
    const res = await apiClient.post<ApiEnvelope<SpinPrizeItem>>(
      API_ENDPOINTS.SPIN.CREATE_PRIZE,
      input
    );
    return res.data.data;
  }

  async updatePrize(
    id: string,
    input: UpdateSpinPrizeInput
  ): Promise<SpinPrizeItem> {
    const res = await apiClient.put<ApiEnvelope<SpinPrizeItem>>(
      API_ENDPOINTS.SPIN.UPDATE_PRIZE(id),
      input
    );
    return res.data.data;
  }

  async removePrize(id: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.SPIN.DELETE_PRIZE(id));
  }

  async togglePrizeActive(
    id: string
  ): Promise<{ id: string; isActive: boolean }> {
    const res = await apiClient.patch<
      ApiEnvelope<{ id: string; isActive: boolean }>
    >(API_ENDPOINTS.SPIN.TOGGLE_PRIZE_ACTIVE(id));
    return res.data.data;
  }

  /* Wins */
  async getWins(params: GetSpinWinsParams = {}): Promise<SpinWinsResponse> {
    const res = await apiClient.get<ApiEnvelope<SpinWinsResponse>>(
      API_ENDPOINTS.SPIN.GET_WINS,
      { params }
    );
    return res.data.data;
  }

  async getWin(id: string): Promise<SpinWinDetail> {
    const res = await apiClient.get<ApiEnvelope<SpinWinDetail>>(
      API_ENDPOINTS.SPIN.GET_WIN(id)
    );
    return res.data.data;
  }

  async getWinStats(): Promise<SpinWinStats> {
    const res = await apiClient.get<ApiEnvelope<SpinWinStats>>(
      API_ENDPOINTS.SPIN.GET_WIN_STATS
    );
    return res.data.data;
  }

  async updateWinStatus(
    id: string,
    status: SpinWinStatus
  ): Promise<SpinWinListItem> {
    const res = await apiClient.patch<ApiEnvelope<SpinWinListItem>>(
      API_ENDPOINTS.SPIN.UPDATE_WIN_STATUS(id),
      { status }
    );
    return res.data.data;
  }
}

const spinService = new SpinService();
export default spinService;